import { Card, CardHeader, Badge } from "../../../components";

interface SCRComparison {
  enabled: boolean;
  nox_reduction_pct: number;
  without_scr: { ng_DALYs: number; ng_PDFs: number; ng_USD: number };
  with_scr: { ng_DALYs: number; ng_PDFs: number; ng_USD: number };
  scr_benefit: {
    DALYs_avoided: number;
    PDFs_avoided: number;
    USD_avoided: number;
    daly_pct_reduction: number;
    pdf_pct_reduction: number;
  };
  midpoint_reductions?: Record<
    string,
    { net_reduction: number; daly_delta: number; pdf_delta: number }
  >;
}

interface Props {
  comparison: SCRComparison;
}

const MIDPOINT_META: Record<string, { label: string; unit: string }> = {
  "particulate matter formation_kg PM2.5-Eq": {
    label: "Particulate Matter",
    unit: "Tonnes PM2.5-Eq",
  },
  "photochemical oxidant formation: human health_kg NOx-Eq": {
    label: "Photochemical Oxidants (Health)",
    unit: "Tonnes NOx-Eq",
  },
  "photochemical oxidant formation: terrestrial ecosystems_kg NOx-Eq": {
    label: "Photochemical Oxidants (Ecosystems)",
    unit: "Tonnes NOx-Eq",
  },
  "acidification: terrestrial_kg SO2-Eq": {
    label: "Terrestrial Acidification",
    unit: "Tonnes SO\u2082-Eq",
  },
};

function formatUSD(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(1)}K`;
  return `$${abs.toFixed(2)}`;
}

function formatSci(n: number): string {
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 0.01) return abs.toFixed(4);
  return abs.toExponential(2);
}

/** Convert kg to tonnes and format with appropriate precision */
function formatTonnes(kg: number): string {
  const tonnes = Math.abs(kg) / 1000;
  if (tonnes >= 100) return tonnes.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (tonnes >= 1) return tonnes.toFixed(2);
  if (tonnes >= 0.01) return tonnes.toFixed(3);
  if (tonnes >= 0.001) return tonnes.toFixed(4);
  return tonnes.toExponential(2);
}

export function LoadSCRComparisonCard({ comparison }: Props) {
  const { without_scr, with_scr, scr_benefit, nox_reduction_pct } = comparison;

  const rows = [
    {
      label: "Human Health",
      unit: "DALYs",
      without: without_scr.ng_DALYs,
      with: with_scr.ng_DALYs,
      avoided: scr_benefit.DALYs_avoided,
      pct: scr_benefit.daly_pct_reduction,
    },
    {
      label: "Ecosystem Quality",
      unit: "species.yr",
      without: without_scr.ng_PDFs,
      with: with_scr.ng_PDFs,
      avoided: scr_benefit.PDFs_avoided,
      pct: scr_benefit.pdf_pct_reduction,
    },
    {
      label: "Monetized Impact",
      unit: "USD",
      without: without_scr.ng_USD,
      with: with_scr.ng_USD,
      avoided: scr_benefit.USD_avoided,
      pct:
        without_scr.ng_USD > 0
          ? Math.round(
              (scr_benefit.USD_avoided / without_scr.ng_USD) * 1000
            ) / 10
          : 0,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <span className="text-h5 text-text-primary">
            SCR Impact Analysis
          </span>
          <Badge variant="quantum">
            {(nox_reduction_pct * 100).toFixed(0)}% NOx Reduction
          </Badge>
        </div>
        <p className="text-body-sm text-text-secondary mt-1">
          Selective Catalytic Reduction — comparison of NG generation impacts
          with and without emissions controls
        </p>
      </CardHeader>

      <div className="p-4">
        {/* Comparison table */}
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-medium">
                  Impact Category
                </th>
                <th className="text-right py-2 text-text-secondary font-medium">
                  Without SCR
                </th>
                <th className="text-right py-2 text-text-secondary font-medium">
                  With SCR
                </th>
                <th className="text-right py-2 text-text-secondary font-medium">
                  Reduction
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b border-border/50">
                  <td className="py-3">
                    <span className="text-text-primary font-medium">
                      {r.label}
                    </span>
                    <span className="text-text-muted ml-1">({r.unit})</span>
                  </td>
                  <td className="text-right py-3 text-red-400 font-mono">
                    {r.unit === "USD"
                      ? formatUSD(r.without)
                      : formatSci(r.without)}
                  </td>
                  <td className="text-right py-3 text-text-primary font-mono">
                    {r.unit === "USD"
                      ? formatUSD(r.with)
                      : formatSci(r.with)}
                  </td>
                  <td className="text-right py-3">
                    <span className="text-green-400 font-medium">
                      {r.pct > 0 ? `-${r.pct}%` : "0%"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Midpoint breakdown — avoided emissions in tonnes */}
        {comparison.midpoint_reductions &&
          Object.keys(comparison.midpoint_reductions).length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <h4 className="text-body-sm font-medium text-text-secondary mb-2">
                Avoided Emissions by Category (ReCiPe 2016)
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(comparison.midpoint_reductions).map(
                  ([key, val]) => {
                    const meta = MIDPOINT_META[key] || { label: key, unit: "Tonnes" };
                    return (
                      <div
                        key={key}
                        className="bg-surface-600 rounded-lg px-3 py-2"
                      >
                        <div className="text-caption text-text-muted">
                          {meta.label}
                        </div>
                        <div className="text-body-sm text-green-400 font-medium">
                          {formatTonnes(val.net_reduction)} {meta.unit}
                        </div>
                        <div className="text-caption text-text-muted">
                          avoided
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}

        {/* Methodology note */}
        <p className="text-caption text-text-muted mt-4">
          Analysis based on ecoinvent 3.11 LCI elementary flows and ReCiPe 2016
          v1.03 (Hierarchist) characterization factors. NOx reduction applied to
          particulate matter formation, photochemical oxidant formation, and
          terrestrial acidification pathways. NH3 slip trade-off included.
        </p>
      </div>
    </Card>
  );
}
